// src/components/CustomBookingForm.jsx
import React, { useState, useEffect } from "react";
import DatePicker, { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import ja from "date-fns/locale/ja";
import enUS from "date-fns/locale/en-US";
import { addDays, format, differenceInDays } from "date-fns";
import "./CustomBookingForm.css";

registerLocale("ja", ja);
registerLocale("en", enUS);

const texts = {
    ja: {
        title: "ご宿泊予約",
        notice: "※当施設は最低2泊からご予約を承っております。",
        notice9plus: "※9名様以上でのご宿泊をご希望の場合は、別途お問い合わせください。",
        loading: "空室状況を確認中...",
        checkIn: "チェックイン:",
        checkOut: "チェックアウト:",
        dateFormat: "yyyy年MM月dd日",
        btnSubmit: "お客様情報の入力へ",
        step2Title: "お客様情報の入力",
        summary: "ご予約内容",
        nights: "泊",
        name: "お名前（フルネーム）",
        email: "メールアドレス",
        phone: "電話番号",
        guests: "宿泊人数",
        coupon: "クーポンコード（任意）", // ←追加
        btnBack: "日付選択に戻る",
        btnConfirm: "予約をリクエストする",
        totalPrice: "合計金額 (税込):",
        minNightsError: "※最低2泊以上でご選択ください",
        calculating: "Beds24から最新料金を取得中...",
        successTitle: "ご予約ありがとうございます！",
        successMsg: "決済およびご予約が正常に完了いたしました。",
        bookingId: "予約番号:",
        gettingId: "予約番号を発行中...",
        noticeCancel: "※この予約番号は、予約の確認やキャンセルの際に必要となります。必ずお控えください。",
        toLookup: "ご予約内容の確認・キャンセルページへ",
    },
    en: {
        title: "Book Your Stay",
        notice: "* Minimum 2 nights stay required.",
        notice9plus: "* For bookings of 9 or more guests, please contact us directly.",
        loading: "Checking availability...",
        checkIn: "Check-in:",
        checkOut: "Check-out:",
        dateFormat: "MMM d, yyyy",
        btnSubmit: "Enter Guest Details",
        step2Title: "Guest Information",
        summary: "Booking Summary",
        nights: "Nights",
        name: "Full Name",
        email: "Email Address",
        phone: "Phone Number",
        guests: "Number of Guests",
        coupon: "Coupon Code (Optional)", // ←追加
        btnBack: "Back to Calendar",
        btnConfirm: "Request Booking",
        totalPrice: "Total Price (inc. tax):",
        minNightsError: "* Please select at least 2 nights",
        calculating: "Calculating price...",
        successTitle: "Thank you for your booking!",
        successMsg: "Your payment and reservation have been completed successfully.",
        bookingId: "Booking ID:",
        gettingId: "Issuing Booking ID...",
        noticeCancel: "* Please keep this Booking ID safe, as it will be required to check or cancel your reservation.",
        toLookup: "Manage or Cancel your Booking",
    }
};

const CustomBookingForm = ({ isEnglish }) => {
    const [lang, setLang] = useState(isEnglish ? "en" : "ja");

    useEffect(() => {
        setLang(isEnglish ? "en" : "ja");
    }, [isEnglish]);

    const t = texts[lang];
    const [step, setStep] = useState(1);

    const [availability, setAvailability] = useState({});
    const [isLoading, setIsLoading] = useState(true);
    const [dateRange, setDateRange] = useState([null, null]);
    const [startDate, endDate] = dateRange;
    const [totalPrice, setTotalPrice] = useState(0);
    const [isCalculating, setIsCalculating] = useState(false);

    // ★ couponCode を state に追加
    const [guestInfo, setGuestInfo] = useState({ name: "", email: "", phone: "", guests: 2, couponCode: "" });
    const [finalBookingId, setFinalBookingId] = useState(null);

    const nights = startDate && endDate ? differenceInDays(endDate, startDate) : 0;
    const isValidNights = nights >= 2;
    const formatDateString = (date) => format(date, "yyyy-MM-dd");

    useEffect(() => {
        const fullUrl = window.location.href;
        const isSuccess = fullUrl.includes("status=success");

        const match = fullUrl.match(/session_id=([^&]+)/);
        const sessionId = match ? match[1] : null;

        if (isSuccess) {
            setStep(3);
            setIsLoading(false);

            setTimeout(() => {
                const formElement = document.querySelector(".booking-form-container");
                if (formElement) {
                    formElement.scrollIntoView({ behavior: "smooth", block: "center" });
                }
            }, 500);

            if (sessionId) {
                let attempts = 0;
                const fetchBookingId = async () => {
                    try {
                        const res = await fetch(`/api/getCheckoutSession?session_id=${sessionId}`);
                        const data = await res.json();
                        if (data.success && data.metadata && data.metadata.beds24_book_id) {
                            setFinalBookingId(data.metadata.beds24_book_id);
                        } else if (attempts < 5) {
                            attempts++;
                            setTimeout(fetchBookingId, 2000);
                        }
                    } catch (err) {
                        console.error("Booking ID取得エラー", err);
                    }
                };
                fetchBookingId();
            }
        } else {
            // ▼ 過去の日付（2024年以降など）に対応できるよう開始日を調整（必要に応じて）
            fetch("/api/getAvailability?startDate=2024-01-01&endDate=2026-12-31")
                .then(res => res.json())
                .then(json => {
                    if (json.success && json.data && json.data.length > 0) setAvailability(json.data[0].availability);
                    setIsLoading(false);
                });
        }
    }, []);

    useEffect(() => {
        const fetchQuote = async () => {
            if (startDate && endDate && isValidNights) {
                setIsCalculating(true);
                try {
                    const res = await fetch("/api/getQuote", {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({ checkIn: formatDateString(startDate), checkOut: formatDateString(endDate), guests: guestInfo.guests }),
                    });
                    const data = await res.json();
                    setTotalPrice(data.success ? data.totalAmount : 0);
                } catch (e) {
                    setTotalPrice(0);
                } finally {
                    setIsCalculating(false);
                }
            } else setTotalPrice(0);
        };
        fetchQuote();
    }, [startDate, endDate, guestInfo.guests, isValidNights]);

    const filterDates = (date) => {
        if (!startDate || (startDate && endDate)) {
            const dateStr1 = formatDateString(date);
            const dateStr2 = formatDateString(addDays(date, 1));
            return availability[dateStr1] === true && availability[dateStr2] === true;
        }
        if (date < startDate) return false;
        let currentDate = startDate;
        while (currentDate < date) {
            if (availability[formatDateString(currentDate)] === false) return false;
            currentDate = addDays(currentDate, 1);
        }
        return true;
    };

    const handleSubmitBooking = async (e) => {
        e.preventDefault();
        try {
            // ★ guestInfoにクーポンコードも含めてAPIへ送信
            const res = await fetch("/api/createCheckoutSession", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    checkIn: formatDateString(startDate),
                    checkOut: formatDateString(endDate),
                    lang: lang,
                    ...guestInfo
                }),
            });
            const data = await res.json();
            if (data.success) window.location.href = data.url;
            else alert(isEnglish ? "Failed to prepare payment screen." : "決済画面の準備に失敗しました。");
        } catch (error) {
            alert(isEnglish ? "A network error occurred." : "通信エラーが発生しました。");
        }
    };

    return (
        <div className="booking-form-container">
            {step === 1 && (
                <div className="step-content fade-in">
                    <h2 className="booking-form-title">{t.title}</h2>
                    {isLoading ? <p className="loading-text">{t.loading}</p> : (
                        <div className="calendar-group">
                            <p className="notice-text">{t.notice}</p>

                            <div className="step1-guests" style={{ marginBottom: "5px" }}>
                                <label style={{ marginRight: "10px" }}>{t.guests}</label>
                                <select name="guests" value={guestInfo.guests} onChange={(e) => setGuestInfo({ ...guestInfo, guests: e.target.value })}>
                                    {[1, 2, 3, 4, 5, 6, 7, 8].map(num => <option key={num} value={num}>{num} {isEnglish ? "Guests" : "名"}</option>)}
                                </select>
                            </div>
                            <p className="notice-text" style={{ fontSize: "0.85em", marginTop: "0", marginBottom: "15px", color: "#666" }}>
                                {t.notice9plus}
                            </p>

                            <div className="single-calendar-wrapper">
                                <DatePicker selectsRange startDate={startDate} endDate={endDate} onChange={update => setDateRange(update)} locale={lang} filterDate={filterDates} minDate={new Date()} inline />
                            </div>
                            {startDate && endDate && (
                                <div className="price-display-box fade-in">
                                    <p>{format(startDate, t.dateFormat, { locale: lang === "ja" ? ja : enUS })} 〜 {format(endDate, t.dateFormat, { locale: lang === "ja" ? ja : enUS })} ({nights}{t.nights})</p>
                                    {!isValidNights ? <p className="total-price" style={{ color: "#d9534f" }}>{t.minNightsError}</p> : isCalculating ? <p className="total-price" style={{ color: "#888" }}>{t.calculating}</p> : <p className="total-price">{t.totalPrice} ¥{totalPrice.toLocaleString()}</p>}
                                </div>
                            )}
                            <button className="search-btn" disabled={!startDate || !endDate || !isValidNights || isCalculating || totalPrice <= 0} onClick={() => setStep(2)}>{t.btnSubmit}</button>
                        </div>
                    )}
                </div>
            )}

            {step === 2 && (
                <div className="step-content fade-in">
                    <h2 className="booking-form-title">{t.step2Title}</h2>
                    <div className="summary-card">
                        <h3>{t.summary}</h3>
                        <p>{t.checkIn} <strong>{format(startDate, t.dateFormat, { locale: lang === "ja" ? ja : enUS })}</strong></p>
                        <p>{t.checkOut} <strong>{format(endDate, t.dateFormat, { locale: lang === "ja" ? ja : enUS })}</strong></p>
                        <p className="nights-count">({nights}{t.nights} / {guestInfo.guests}{isEnglish ? " Guests" : "名"})</p>
                        <p style={{ marginTop: "10px", fontWeight: "bold", color: "#b76e79" }}>{t.totalPrice} ¥{totalPrice.toLocaleString()}</p>
                    </div>
                    <form className="guest-form" onSubmit={handleSubmitBooking}>
                        <div className="form-group"><label>{t.name}</label><input type="text" name="name" required value={guestInfo.name} onChange={e => setGuestInfo({ ...guestInfo, name: e.target.value })} /></div>
                        <div className="form-group"><label>{t.email}</label><input type="email" name="email" required value={guestInfo.email} onChange={e => setGuestInfo({ ...guestInfo, email: e.target.value })} /></div>
                        <div className="form-group"><label>{t.phone}</label><input type="tel" name="phone" required value={guestInfo.phone} onChange={e => setGuestInfo({ ...guestInfo, phone: e.target.value })} /></div>



                        <div className="form-actions">
                            <button type="button" className="back-btn" onClick={() => setStep(1)}>{t.btnBack}</button>
                            <button type="submit" className="submit-btn">{t.btnConfirm}</button>
                        </div>
                    </form>
                </div>
            )}

            {step === 3 && (
                <div className="step-content fade-in" style={{ textAlign: "center", padding: "40px 20px" }}>
                    <h2 style={{ color: "#2E7D32", marginBottom: "15px" }}>{t.successTitle}</h2>
                    <p style={{ fontSize: "1.1em", marginBottom: "30px" }}>{t.successMsg}</p>

                    <div style={{ backgroundColor: "#f9f9f9", padding: "20px", borderRadius: "8px", display: "inline-block", border: "1px solid #ddd" }}>
                        <p style={{ margin: "0", fontSize: "1.2em", color: "#555" }}>{t.bookingId}</p>
                        {finalBookingId ? (
                            <h3 style={{ margin: "10px 0 0", fontSize: "2em", color: "#333", letterSpacing: "2px" }}>{finalBookingId}</h3>
                        ) : (
                            <p style={{ margin: "10px 0 0", color: "#888" }}>{t.gettingId}</p>
                        )}
                    </div>

                    <p style={{ marginTop: "20px", color: "#d9534f", fontSize: "0.9em" }}>{t.noticeCancel}</p>

                    <div style={{ marginTop: "30px" }}>
                        <a
                            href={isEnglish ? "/en/lookup" : "/lookup"}
                            style={{ display: "inline-block", padding: "12px 24px", backgroundColor: "#080752", color: "#fff", textDecoration: "none", borderRadius: "4px", fontWeight: "bold" }}
                        >
                            {t.toLookup}
                        </a>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CustomBookingForm;